import styled from "styled-components"
import { useDataLayerContext } from "./ContextLayer/DataProvider/DataProvider"
import { useSettingsStorageContext } from "./ContextLayer/SettingsStorageProvider"
import { useEffect, useRef } from "react"
import gsap from "gsap";
import $ from "jquery";
import { withMobileChecker } from "../common";

const WordPairAtom = styled.div<{ $darkMode: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0px;
    opacity: 0;
    width: 600px;
    line-height: 1.0;
    font-family: 'Google Sans', 'DM Serif Display', 'FixelVariable', 'Fira Code', sans-serif;
    padding: 5px;
    border-radius: 8px;

    transition: background-color var(--transition-duration-slow);

    @media screen and (max-width: 1200px) {
        width: auto;
    }
`

const ThePair = styled.div<{ $darkMode: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 20px;
    padding: 22px 20px 10px 20px;
    border-radius: 5px;
    background-color: ${props => props.$darkMode ? 'rgba(5, 33, 41, 0.19)' : 'rgba(3, 20, 27, 0.06)'};
    width: 100%;
    box-sizing: border-box;
    backdrop-filter: blur(${props => props.$darkMode ? '6px' : '3px'});

    transition: background-color var(--transition-duration-slow);

    @media screen and (max-width: 1200px) {
        background-color: ${props => props.$darkMode ? 'rgba(5, 33, 41, 0.19)' : 'rgba(3, 20, 27, 0.06)'};
        backdrop-filter: blur(${props => props.$darkMode ? '3px' : '3px'});
    }
`

const WordAtom = styled.div<{ $darkMode: boolean }>`
    font-size: 2.8rem;
    font-weight: 500;
    letter-spacing: -0.5px;
    color: ${[props => props.$darkMode ? '#FEF7E2' : '#555351']};
    cursor: default;

    transition: color var(--transition-duration-slow);

    @media screen and (max-width: 1200px) {
        font-size: 2.0rem;
        font-weight: 400;
    }
`

const DividerAtom = styled.div<{ $darkMode: boolean }>`
    height: 1px;
    width: 100%;
    background-color: ${props => props.$darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(149, 84, 15, 0.1)'};
`

const DescriptionAtom = styled.div<{ $darkMode: boolean }>`
    font-size: 2.5rem;
    font-weight: 400;
    letter-spacing: 0.8px;
    font-family: 'Google Sans', 'FixelVariable', 'Fira Code', sans-serif;
    color: ${[props => props.$darkMode ? '#FEF7E2' : '#555351']};
    filter: blur(7px);
    opacity: 0.6;
    transition: filter var(--transition-duration-slow), opacity var(--transition-duration-slow), color var(--transition-duration-slow);
    cursor: default;

    &.clear {
        filter: blur(0px);
        opacity: 1;
    }

    &:hover { 
        filter: blur(0px);
        opacity: 1;
    }

    @media screen and (max-width: 1200px) {
        font-size: 1.9rem;
    }
`

const ExamplesAtom = styled.div<{ $darkMode: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 6px;
    opacity: 0;
    line-height: 1.0;
    font-size: 0.9rem;
    font-weight: 600;
    padding: 30px 20px 20px 20px;
    box-sizing: border-box;
    backdrop-filter: blur(${props => props.$darkMode ? '6px' : '4px'});
    background: ${props => props.$darkMode ? 'linear-gradient(180deg, rgba(2, 17, 34, 0.22) 0%, rgba(0, 0, 0, 0.03) 15%, rgba(0, 0, 0, 0.03) 100%)' : 'linear-gradient(180deg, rgba(73, 63, 4, 0.15) 0%, rgba(2, 20, 30, 0.02) 15%, rgba(2, 20, 30, 0.00) 100%)'};
    border-radius: 0px 0px 5px 5px;

    & div.italianExample {
        color: ${props => props.$darkMode ? '#FEF7E2' : '#555351'};
        transition: color var(--transition-duration-slow);
    }

    & div.englishExample {
        color: ${props => props.$darkMode ? '#ffffff' : '#555351'};
        font-size: 0.9rem;
        font-weight: 400;
        overflow: hidden;
        opacity: ${props => props.$darkMode ? 0.6 : 0.8};
        margin-bottom: 18px;

        transition: color var(--transition-duration-slow), opacity var(--transition-duration-slow);
    }

    & div.englishExample:last-child {
        margin-bottom: 0px;
    }

    @media screen and (max-width: 1200px) {
        background: transparent;
        backdrop-filter: blur(${props => props.$darkMode ? '3px' : '4px'});
        font-size: 0.8rem;
        line-height: 0.9rem;
        width: 100%;

        & div.englishExample {
            font-size: 0.8rem;
        }
    }

`

export default function WordPair() {
    const { dataLayer } = useDataLayerContext();
    const { settings } = useSettingsStorageContext();
    const isMobile = withMobileChecker();

    const wordRef = useRef<HTMLDivElement>(null);
    const wordPairRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const examplesRef = useRef<HTMLDivElement>(null);
    const revealTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const windowFocusHandler = () => {
            $("#wordPairDescription").removeClass("clear");
            if (revealTimer.current) {
                clearTimeout(revealTimer.current);
            }
            revealTimer.current = setTimeout(() => {
                if (!$("#wordPairDescription").hasClass("clear")) $("#wordPairDescription").addClass("clear");
            }, 10000);
        };

        window.addEventListener('focus', windowFocusHandler);

        return () => {
            if (revealTimer.current) {
                clearTimeout(revealTimer.current);
            }
            window.removeEventListener('focus', windowFocusHandler);
        }
    }, []);

    useEffect(() => {
        const globalClick = (e: MouseEvent) => {
            let targetElement = (e.target as HTMLElement);

            if (targetElement.getAttribute('id') === 'mainPanel') {
                if (revealTimer.current) {
                    clearTimeout(revealTimer.current);
                }
                if (!$("#wordPairDescription").hasClass("clear")) $("#wordPairDescription").addClass("clear");
            }
        }

        document.addEventListener('click', globalClick);

        return () => {
            document.removeEventListener('click', globalClick);
        }
    }, []);

    useEffect(() => {
        if (!dataLayer?.currentIteration?.currentDictionaryEntry?.word || !dataLayer?.currentIteration?.currentDictionaryEntry?.description) return;

        gsap.to("#wordPair", {
            opacity: 0,
            duration: 0.6,
            ease: "power2.inOut",
            onComplete: () => {
                $("#wordPairDescription").removeClass("clear");
                wordRef.current!.innerText = settings.currentLanguage === 'foreign' ? dataLayer?.currentIteration?.currentDictionaryEntry?.word || "" : dataLayer?.currentIteration?.currentDictionaryEntry?.description || "";
                descriptionRef.current!.innerText = settings.currentLanguage === 'foreign' ? dataLayer?.currentIteration?.currentDictionaryEntry?.description || "" : dataLayer?.currentIteration?.currentDictionaryEntry?.word || "";

                examplesRef.current!.innerText = "";
                if (dataLayer?.currentIteration?.currentDictionaryEntry?.examples && (dataLayer?.currentIteration?.currentDictionaryEntry?.examples?.length || 0) > 0) {
                    dataLayer?.currentIteration?.currentDictionaryEntry?.examples.forEach((example, index) => {
                        if (isMobile && index > 0) return;
                        const div = document.createElement('div');
                        div.setAttribute("id", "italianExample" + index)
                        div.setAttribute("class", "italianExample")
                        div.innerText = example.italian || "";
                        examplesRef.current!.appendChild(div);

                        const divEn = document.createElement('div');
                        divEn.setAttribute("id", "englishExample" + index)
                        divEn.setAttribute("class", "englishExample")
                        divEn.innerText = example.english_translation || "";
                        examplesRef.current!.appendChild(divEn);
                    })
                }

                if (examplesRef.current) {
                    examplesRef.current.setAttribute('style', 'opacity: 0');
                }

                gsap.to("#wordPair", {
                    opacity: 1,
                    duration: 0.3,
                    ease: "power2.inOut",
                    onComplete: () => {
                        if (revealTimer.current) {
                            clearTimeout(revealTimer.current);
                        }
                        revealTimer.current = setTimeout(() => {
                            if (!$("#wordPairDescription").hasClass("clear")) $("#wordPairDescription").addClass("clear");
                        }, 10000);

                        gsap.to("#examples", {
                            opacity: 1,
                            duration: 0.6,
                            delay: 0.3,
                            ease: "power2.inOut",
                        });
                    }
                });
            }
        });

        return () => {
            if (revealTimer.current) {
                clearTimeout(revealTimer.current);
            }
        };
    }, [isMobile, dataLayer?.currentIteration?.currentDictionaryEntry?.word, dataLayer?.currentIteration?.currentDictionaryEntry?.description, settings.currentLanguage, dataLayer?.currentIteration?.currentDictionaryEntry?.examples])

    useEffect(() => {
        const keyDownHandler = (e: KeyboardEvent) => {
            if (document.getElementById('quickEntry')?.classList.contains('open') || e.key !== 'Alt') return;

            switch (e.type) {
                case 'keydown':
                    if (!$("#wordPairDescription").hasClass("clear")) $("#wordPairDescription").addClass("clear");
                    break;
                case 'keyup':
                    $("#wordPairDescription").removeClass("clear");
                    break;
            }
        }

        document.addEventListener('keydown', keyDownHandler);
        document.addEventListener('keyup', keyDownHandler);

        return () => {
            document.removeEventListener('keydown', keyDownHandler);
            document.removeEventListener('keyup', keyDownHandler);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataLayer]);

    return (
        <WordPairAtom ref={wordPairRef} id="wordPair" $darkMode={settings.darkMode}>
            <ThePair $darkMode={settings.darkMode}>
                <WordAtom ref={wordRef} $darkMode={settings.darkMode}></WordAtom>
                <DividerAtom $darkMode={settings.darkMode} />
                <DescriptionAtom id="wordPairDescription" $darkMode={settings.darkMode} ref={descriptionRef} style={{ marginBottom: '20px' }}></DescriptionAtom>
            </ThePair>
            {/* <DividerAtom $darkMode={settings.darkMode}/> */}
            <ExamplesAtom id="examples" ref={examplesRef} $darkMode={settings.darkMode}></ExamplesAtom>
        </WordPairAtom>
    )
}